import React, { useEffect, useMemo } from 'react'
import {
	ActionIcon,
	Anchor,
	Button,
	Group,
	MediaQuery,
	MultiSelect,
	Select,
	Stack,
	Text,
	TextInput,
} from '@mantine/core'
import { Plus, X } from 'tabler-icons-react'

export const ADDITIONAL_FILTER_LIMIT = 5

export const STANDARD_FILTER_FIELD_KEYS = {
	accountName: '__check_in_account_name__',
	segment: '__check_in_segment__',
	checkInDate: '__check_in_date__',
	checkOutDate: '__check_out_date__',
}

const STANDARD_FILTER_FIELDS = [
	{ value: STANDARD_FILTER_FIELD_KEYS.accountName, label: 'Account Name' },
	{ value: STANDARD_FILTER_FIELD_KEYS.segment, label: 'Segment' },
	{ value: STANDARD_FILTER_FIELD_KEYS.checkInDate, label: 'Check-in date' },
	{ value: STANDARD_FILTER_FIELD_KEYS.checkOutDate, label: 'Check-out date' },
]

const CHECK_IN_STATUS_OPTIONS = [
	{ value: 'all', label: 'All check-in statuses' },
	{ value: 'checkedIn', label: 'Checked In' },
	{ value: 'notCheckedIn', label: 'Not Checked In' },
]

export const getCheckInCustomFields = (checkIn) => {
	const customFields = checkIn?.customFields
	if (
		!customFields ||
		typeof customFields !== 'object' ||
		Array.isArray(customFields)
	) {
		return {}
	}
	return customFields
}

export const formatCheckInFilterValue = (value) => {
	if (value === null || value === undefined) return ''
	if (typeof value === 'string') return value.trim()
	if (typeof value === 'number' || typeof value === 'boolean') return String(value)
	try {
		return JSON.stringify(value)
	} catch {
		return String(value)
	}
}

const formatDateFilterValue = (value) => {
	if (!value) return ''
	const text = String(value).trim()
	const isoDate = text.match(/^\d{4}-\d{2}-\d{2}/)?.[0]
	if (isoDate) return isoDate

	const date = new Date(value)
	return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
}

export const getCheckInFilterValue = (checkIn, field) => {
	switch (field) {
		case STANDARD_FILTER_FIELD_KEYS.accountName:
			return formatCheckInFilterValue(checkIn?.accountName)
		case STANDARD_FILTER_FIELD_KEYS.segment:
			return formatCheckInFilterValue(
				checkIn?.segmentName ?? checkIn?.segment?.name,
			)
		case STANDARD_FILTER_FIELD_KEYS.checkInDate:
			return formatDateFilterValue(checkIn?.scheduledCheckInDate)
		case STANDARD_FILTER_FIELD_KEYS.checkOutDate:
			return formatDateFilterValue(checkIn?.checkOutDate)
		default:
			return formatCheckInFilterValue(getCheckInCustomFields(checkIn)[field])
	}
}

export const matchesAdditionalCheckInFilters = (checkIn, filters) =>
	filters
		.filter((filter) => filter.field)
		.every((filter) => {
			const value = getCheckInFilterValue(checkIn, filter.field)
			return (
				value &&
				(filter.values.length === 0 || filter.values.includes(value))
			)
		})

const buildAdditionalFilterFields = (checkIns) => {
	const customFieldNames = new Set()
	checkIns.forEach((checkIn) => {
		Object.keys(getCheckInCustomFields(checkIn)).forEach((key) => {
			if (key.trim()) customFieldNames.add(key)
		})
	})

	return [
		...STANDARD_FILTER_FIELDS,
		...Array.from(customFieldNames)
			.sort((a, b) => a.localeCompare(b))
			.map((key) => ({ value: key, label: key })),
	]
}

const buildAdditionalFilterValues = (checkIns, fields) => {
	const valuesByField = Object.fromEntries(
		fields.map((field) => [field.value, new Set()]),
	)

	checkIns.forEach((checkIn) => {
		fields.forEach((field) => {
			const value = getCheckInFilterValue(checkIn, field.value)
			if (value) valuesByField[field.value].add(value)
		})
	})

	return Object.fromEntries(
		Object.entries(valuesByField).map(([field, values]) => [
			field,
			Array.from(values)
				.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
				.map((value) => ({ value, label: value })),
		]),
	)
}

const createFilterId = () =>
	`check-in-filter-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const AdditionalFilterRows = ({
	checkIns,
	filters,
	onChange,
	isMobile = false,
	emphasizeAddFilter = false,
}) => {
	const fieldOptions = useMemo(
		() => buildAdditionalFilterFields(checkIns),
		[checkIns],
	)
	const valueOptionsByField = useMemo(
		() => buildAdditionalFilterValues(checkIns, fieldOptions),
		[checkIns, fieldOptions],
	)

	useEffect(() => {
		if (filters.length > ADDITIONAL_FILTER_LIMIT) {
			onChange(filters.slice(0, ADDITIONAL_FILTER_LIMIT))
			return
		}
		if (checkIns.length === 0) return

		const validFields = new Set(fieldOptions.map((option) => option.value))
		const nextFilters = filters.map((filter) => {
			if (!filter.field || !validFields.has(filter.field)) {
				return filter.field || filter.values.length > 0
					? { ...filter, field: '', values: [] }
					: filter
			}

			const availableValues = new Set(
				(valueOptionsByField[filter.field] || []).map((option) => option.value),
			)
			const values = filter.values.filter((value) => availableValues.has(value))
			return values.length === filter.values.length ? filter : { ...filter, values }
		})

		if (nextFilters.some((filter, index) => filter !== filters[index])) {
			onChange(nextFilters)
		}
	}, [checkIns.length, fieldOptions, filters, onChange, valueOptionsByField])

	const addFilter = () => {
		if (filters.length >= ADDITIONAL_FILTER_LIMIT) return
		onChange([...filters, { id: createFilterId(), field: '', values: [] }])
	}

	const updateFilter = (filterId, updates) => {
		onChange(
			filters.map((filter) =>
				filter.id === filterId ? { ...filter, ...updates } : filter,
			),
		)
	}
	const AddFilterControl = emphasizeAddFilter ? Button : Anchor
	const addFilterControlProps = emphasizeAddFilter
		? { variant: 'light', leftIcon: <Plus size={16} aria-hidden='true' /> }
		: { component: 'button' }

	return (
		<Stack spacing='xs'>
			{filters.map((filter) => {
				const valueOptions = filter.field
					? valueOptionsByField[filter.field] || []
					: []

				return (
					<Group
						key={filter.id}
						spacing='sm'
						align='flex-end'
						noWrap={!isMobile}
					>
						<Text
							size='sm'
							fw={600}
							mb={8}
							style={{ width: 112, flexShrink: 0 }}
						>
							Filter
						</Text>
						<Select
							placeholder='Filter field'
							value={filter.field}
							onChange={(field) =>
								updateFilter(filter.id, { field: field || '', values: [] })
							}
							data={fieldOptions}
							searchable
							clearable
							style={{ flex: isMobile ? '1 1 180px' : '0 0 280px' }}
						/>
						<MultiSelect
							placeholder={
								filter.field
									? `All ${
										fieldOptions.find((option) => option.value === filter.field)
											?.label || filter.field
									  } values`
									: 'Select field first'
							}
							value={filter.values}
							onChange={(values) => updateFilter(filter.id, { values })}
							data={valueOptions}
							searchable
							clearable
							disabled={!filter.field || valueOptions.length === 0}
							style={{ flex: '1 1 260px' }}
						/>
						<ActionIcon
							variant='subtle'
							color='gray'
							aria-label='Remove filter'
							title='Remove filter'
							onClick={() =>
								onChange(filters.filter((item) => item.id !== filter.id))
							}
							mb={4}
						>
							<X size={16} />
						</ActionIcon>
					</Group>
				)
			})}
			{filters.length < ADDITIONAL_FILTER_LIMIT && (
				<AddFilterControl
					{...addFilterControlProps}
					type='button'
					size='sm'
					onClick={addFilter}
					style={{ alignSelf: 'flex-start' }}
				>
					Add Filter
				</AddFilterControl>
			)}
		</Stack>
	)
}

const DedicatedFilter = ({ filter, isMobile }) => {
	if (filter.type === 'multiSelect') {
		return (
			<MultiSelect
				label={isMobile ? undefined : filter.label}
				placeholder={filter.placeholder}
				value={filter.value}
				onChange={filter.onChange}
				data={filter.data}
				searchable={filter.searchable}
				clearable={filter.clearable}
			/>
		)
	}

	return (
		<Select
			label={isMobile ? undefined : filter.label}
			placeholder={filter.placeholder}
			value={filter.value}
			onChange={filter.onChange}
			data={filter.data}
			searchable={filter.searchable}
			clearable={filter.clearable}
		/>
	)
}

const CheckInFilterFields = ({
	isMobile,
	searchTerm,
	onSearchTermChange,
	searchPlaceholder,
	checkInStatus,
	onCheckInStatusChange,
	stationFilter,
	onStationFilterChange,
	stationOptions,
	itemFilter,
	onItemFilterChange,
	itemOptions,
	extraDedicatedFilters,
}) => (
	<>
		<TextInput
			label={isMobile ? undefined : 'Search'}
			placeholder={searchPlaceholder}
			value={searchTerm}
			onChange={(event) => onSearchTermChange(event.currentTarget.value)}
		/>
		<Select
			label={isMobile ? undefined : 'Check-in status'}
			placeholder='Check-in status'
			value={checkInStatus}
			onChange={(value) => onCheckInStatusChange(value || 'all')}
			data={CHECK_IN_STATUS_OPTIONS}
		/>
		{extraDedicatedFilters.map((filter) => (
			<DedicatedFilter
				key={filter.key || filter.label}
				filter={filter}
				isMobile={isMobile}
			/>
		))}
		<MultiSelect
			label={isMobile ? undefined : 'Stations'}
			placeholder='All stations (any match)'
			value={stationFilter}
			onChange={onStationFilterChange}
			data={stationOptions}
			searchable
			clearable
		/>
		<MultiSelect
			label={isMobile ? undefined : 'Purchased Items'}
			placeholder='All purchased items (any match)'
			value={itemFilter}
			onChange={onItemFilterChange}
			data={itemOptions}
			searchable
			clearable
		/>
	</>
)

const CheckInFilterBlock = ({
	checkIns,
	searchTerm,
	onSearchTermChange,
	searchPlaceholder = 'Search name, email, phone, booking...',
	checkInStatus,
	onCheckInStatusChange,
	stationFilter,
	onStationFilterChange,
	stationOptions,
	itemFilter,
	onItemFilterChange,
	itemOptions,
	filters,
	onFiltersChange,
	extraDedicatedFilters = [],
	marginBottom = 'md',
	emphasizeAddFilter = false,
}) => {
	const fieldProps = {
		searchTerm,
		onSearchTermChange,
		searchPlaceholder,
		checkInStatus,
		onCheckInStatusChange,
		stationFilter,
		onStationFilterChange,
		stationOptions,
		itemFilter,
		onItemFilterChange,
		itemOptions,
		extraDedicatedFilters,
	}

	return (
		<>
			<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
				<Stack mb={marginBottom} spacing='xs'>
					<Group spacing='sm' grow>
						<CheckInFilterFields {...fieldProps} isMobile={false} />
					</Group>
					<AdditionalFilterRows
						checkIns={checkIns}
						filters={filters}
						onChange={onFiltersChange}
						emphasizeAddFilter={emphasizeAddFilter}
					/>
				</Stack>
			</MediaQuery>

			<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
				<Stack mb={marginBottom} spacing='xs'>
					<CheckInFilterFields {...fieldProps} isMobile />
					<AdditionalFilterRows
						checkIns={checkIns}
						filters={filters}
						onChange={onFiltersChange}
						emphasizeAddFilter={emphasizeAddFilter}
						isMobile
					/>
				</Stack>
			</MediaQuery>
		</>
	)
}

export default CheckInFilterBlock
